import {cdnImage} from '@/util/cdnImage';
import {MUIContainer} from '@/components/MUI-Components';
import {POLKADOT_BLOCKCHAIN_DEVELOPMENT} from '@/util/metaData/blockchain';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import dynamic from 'next/dynamic';
import PageHead from '@/components/PageHead';
import React from 'react';

// Above-the-fold sections are imported statically, not with next/dynamic: a dynamic section renders
// nothing until its chunk arrives after an in-site navigation, then pushes the page down (CLS).
import Banner from '@/components/Ui-components/Banner';
import DisplayImage from '@/components/Ui-components/display-image';

// ─── getStaticProps ─────────────────────────────────────────────────────────
export const getStaticProps = async () => {
    const {
        algemCheckPoint,
        flexiblePolkadotContent,
        palkadotBannerBottom,
        palkadotBlockchainFAQ,
        palkadotBlockchainService,
        palkadotChooseBody,
        palkadotChooseContent,
        palkadotDecodingData,
        palkadotDevelopmentProcess,
        palkadotExclusiveFeature,
        palkadotKeyServiceData,
        palkodotTestimonial,
        polkadotIndustryEngineering,
        polkadotInnovationData,
        polkadotTechnologyData,
        polkaswapCheckPoint,
        zeitgeistCheckPoint
    } = await import('@/util/contents/polkadot-blockchain');

    return {
        props: {
            algemCheckPoint,
            flexiblePolkadotContent,
            palkadotBannerBottom,
            palkadotBlockchainFAQ,
            palkadotBlockchainService,
            palkadotChooseBody,
            palkadotChooseContent,
            palkadotDecodingData,
            palkadotDevelopmentProcess,
            palkadotExclusiveFeature,
            palkadotKeyServiceData,
            palkodotTestimonial,
            polkadotIndustryEngineering,
            polkadotInnovationData,
            polkadotTechnologyData,
            polkaswapCheckPoint,
            zeitgeistCheckPoint
        }
    };
};

const Decoding = dynamic(() => import('@/components/Ui-components/Decoding'));
const auditImg = cdnImage('Polkadot-Blockchain-Development-Company/Polkadot-Blockchain-Development.webp');
const HelperSection = dynamic(() => import('@/components/Ui-components/helper-section'));
const LatestCTA = dynamic(() => import('@/components/Ui-components/LatestCta'));
const Platforms = dynamic(() => import('@/components/Ui-components/platforms'));
const OurServices = dynamic(() => import('@/components/Ui-components/our-services'));
const CardTechnology = dynamic(() => import('@/components/Ui-components/card-technology'));
const DynamicScreens = dynamic(() => import('@/components/Ui-components/Dynamic-screens'));
const MobileProcess = dynamic(() => import('@/components/Ui-components/MobileProcess'));
const development = cdnImage('Polkadot-Blockchain-Development-Company/Polkadot-Blockchain-Development-Process.svg');
const OurMakeChoice = dynamic(() => import('@/components/Ui-components/OurMakeChoice'));
const LetsDiscuss = dynamic(() => import('@/components/Ui-components/Lets-discuss'));
const Questions = dynamic(() => import('@/components/plain-sections/Questions'));
const Testimonials = dynamic(() => import('@/components/Ui-components/Testimonials'));
const FlexibleCards = dynamic(() => import('@/components/Ui-components/FlexibleCards'));
const zeitgiest = cdnImage('Polkadot-Blockchain-Development-Company/Zeitgeist.webp');
const algem = cdnImage('Polkadot-Blockchain-Development-Company/Algem.webp');
const polkaswap = cdnImage('Polkadot-Blockchain-Development-Company/Polkaswap.webp');

// Migrated from globals.scss `.palkadot-blockchain-decoding-wrapper img` (sole
// consumer — this page only). Applied via <Decoding>'s existing `imgSx` passthrough
// prop, which forwards directly to the rendered image.
const palkadotBlockchainDecodingImgSx = {
    width: '450px !important' // no exact token match
} as const;

const PolkadotBlockchain = (props: any) => {
    const {
        openForm,
        algemCheckPoint,
        flexiblePolkadotContent,
        palkadotBannerBottom,
        palkadotBlockchainFAQ,
        palkadotBlockchainService,
        palkadotChooseBody,
        palkadotChooseContent,
        palkadotDecodingData,
        palkadotDevelopmentProcess,
        palkadotExclusiveFeature,
        palkadotKeyServiceData,
        palkodotTestimonial,
        polkadotIndustryEngineering,
        polkadotInnovationData,
        polkadotTechnologyData,
        polkaswapCheckPoint,
        zeitgeistCheckPoint
    } = props;
    return (
        <Box>
            <PageHead meta={POLKADOT_BLOCKCHAIN_DEVELOPMENT} url="/polkadot-blockchain-development" />
            <Banner
                priority={true}
                handleClickOpen={openForm}
                heading="Polkadot Blockchain Development Company"
                subheading="Struggling with fragmented blockchain ecosystems? We specialize in Polkadot development to deliver scalable, interoperable solutions for seamless cross-chain communication. Overcome bottlenecks in connectivity, enhance transaction efficiency, and build future-ready blockchain networks tailored to your business goals."
                image={cdnImage('Polkadot-Blockchain-Development-Company/Polkadot-Blockchain-Development-Company.webp')}
                // bannerBackground="polkadot-bockchain-banner-background"
                alt="Polkadot Blockchain Development Company"
                button="Get Free Consultation"
            />
            <Box className="sub-banner-wrapper sub-banner-bottom-wrapper">
                <MUIContainer>
                    <Grid container spacing={2} alignItems="center" justifyContent="space-between" className="white-label-bottom-container">
                        <Grid item md={2} lg={1.7} sm="auto" xs={12}>
                            <Typography className="banner-bottom-heading">Our Strategic Alliances</Typography>
                        </Grid>
                        {palkadotBannerBottom?.map((data: any, index: number) => (
                            <Grid item sm="auto" xs={6} className="sub-banner-images" key={index + 1}>
                                <Box className="images-banner-bottom">
                                    <DisplayImage src={data.src} alt={data.alt} />
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </MUIContainer>
            </Box>
            <Grid item xs={12} className="bottom-margin top-space decording-main-wrapper mobile-top-padding mobile-bottom-margin">
                <MUIContainer>
                    <Decoding
                        imageSrc={auditImg}
                        alt="Polkadot Blockchain Development: Transforming Interoperability for Your Business"
                        heading="Polkadot Blockchain Development: Transforming Interoperability for Your Business"
                        subHeading={palkadotDecodingData}
                        isSocialMediaBtn={true}
                        className="palkadot-blockchain-decoding-wrapper"
                        imgSx={palkadotBlockchainDecodingImgSx}
                    />
                </MUIContainer>
            </Grid>
            <Grid item xs={12} className="helper-section helper_section_parent ">
                <Box className=" bottom-spacing top-space mobile-top-padding mobile-bottom-padding">
                    <MUIContainer>
                        <Box className="spectrum-header-wrapper solutions">
                            <Typography className="spectrum-heading color-black our-service-main-heading text-align-left" component="h2">
                                Polkadot Blockchain Development Services for Enterprise-Grade Solutions
                            </Typography>
                            <Box className="divder-wrapper">
                                <hr className="divider-line divider-line-left" />
                            </Box>
                            <Typography className="spectrum-sub-heading color-black" component="p">
                                Our Polkadot Blockchain Development Services help businesses build scalable, interoperable solutions
                                tailored to enterprise needs. From secure cross-chain communication to efficient dApp development, we
                                deliver customized Polkadot solutions that enhance connectivity, streamline operations, and support business
                                growth. Partner with our experts to harness the potential of Polkadot&apos;s advanced blockchain ecosystem.
                            </Typography>
                        </Box>
                        <Grid container spacing={2}>
                            <HelperSection data={palkadotBlockchainService} className="home-helper-card" />
                        </Grid>
                    </MUIContainer>
                </Box>
            </Grid>
            <Box className="bottom-spacing top-space mobile-top-padding mobile-bottom-padding">
                <LatestCTA
                    heading="Simplify Cross-Chain Connectivity With Polkadot-Driven Custom Solutions"
                    buttonText="Get Free Consultation"
                    openForm={openForm}
                    className="blue-background-cta"
                />
            </Box>
            <Grid
                item
                xs={12}
                className="blockchain-revolution-service helper_section_parent  bottom-spacing mobile-top-padding mobile-bottom-padding top-space"
            >
                <MUIContainer>
                    <Platforms
                        data={polkadotInnovationData}
                        heading="Polkadot Blockchain Development: The Core Pillars of Innovation"
                        subheading="Polkadot’s advanced architecture redefines blockchain efficiency and interoperability. From its Layer-1 parachain technology to seamless scalability, robust security, and forkless governance, Polkadot offers a flexible and resilient ecosystem. Its customizable frameworks enable cost-effective blockchain development, empowering businesses to create reliable, high-performance decentralized applications tailored to their needs."
                        containerClass="blockchain-card-container"
                        className="defi-feature-platform-service no-border"
                    />
                </MUIContainer>
            </Grid>
            <Box className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding">
                <MUIContainer>
                    <Platforms
                        heading="Key Features of Polkadot Blockchain for Development"
                        subheading="Discover the key features of Polkadot Blockchain that make it a preferred choice for businesses. From parachains to interoperability, these features ensure flexibility, scalability, and robust functionality for developing advanced blockchain solutions. Let’s explore the essential components that power custom Polkadot development and drive innovation."
                        data={palkadotKeyServiceData}
                        className="defi-feature-platform-service"
                    />
                </MUIContainer>
            </Box>
            <Box className="bottom-spacing  mobile-bottom-padding">
                <LatestCTA
                    heading="Want To Transform Your Blockchain Vision into a Real Successful Solution?"
                    buttonText="Share Your Vision"
                    openForm={openForm}
                    className="blue-background-cta"
                />
            </Box>
            <Grid item xs={12} className="helper-section">
                <Box className="helper_section_parent bottom-spacing top-space mobile-top-padding">
                    <Grid container spacing={2} className="our-service-defi-wrapper">
                        <OurServices
                            ourServices={palkadotExclusiveFeature}
                            className="service-shadow-border-box enterprise-key-card"
                            containerClass="card-heading-spacing"
                            heading="Transformative Advantages of Polkadot Blockchain Development For Enterprises"
                            subheading="Polkadot Blockchain empowers enterprises with a robust ecosystem for building scalable, secure, and interconnected solutions. With features like parachains, seamless interoperability, and customizable frameworks, it’s tailored to address complex business challenges while fostering innovation and growth."
                        />
                    </Grid>
                </Box>
            </Grid>
            <Grid item xs={12} className="top-space bottom-spacing  mobile-top-padding generative-tech-wrapper mobile-bottom-padding">
                <CardTechnology
                    heading="Tools & Technologies for Polkadot Blockchain Development"
                    subheading="We employ advanced tools and frameworks to deliver scalable, secure, and efficient Polkadot blockchain solutions. Our expertise ensures seamless development tailored to your business needs."
                    data={polkadotTechnologyData}
                />
            </Grid>
            <Grid item xs={12} className="dynmaic-white-label-wrapper  bottom-margin dynamic-bep20-wrapper mobile-bottom-margin ">
                <MUIContainer>
                    <Box className="spectrum-header-wrapper solutions">
                        <Typography className="spectrum-heading color-black " component="h2">
                            PORTFOLIO
                        </Typography>
                        <Box className="divder-wrapper">
                            <hr className="divider-line" />
                        </Box>
                    </Box>
                    <DynamicScreens
                        imageSrc={zeitgiest}
                        subHeading="Zeitgeist"
                        heading="Enhancing Prediction Markets with Polkadot Blockchain Technology"
                        subPoints={[
                            'We improved Zeitgeist’s prediction market platform by optimizing the creation, trading, and resolution of financial contracts. Using custom Polkadot Blockchain Solutions, we enhanced its ability to handle complex prediction scenarios efficiently. Zeitgeist now delivers a seamless experience for users in decentralized prediction markets.'
                        ]}
                        reverseOrder={true}
                        alt="Zeitgeist"
                        checkItems={zeitgeistCheckPoint}
                        openForm={openForm}
                        imageClass="dynamic-polkadot-image"
                    />
                    <DynamicScreens
                        imageSrc={algem}
                        subHeading="Algem"
                        heading="Advancing Liquid Staking Solution on the Astar Network"
                        subPoints={[
                            'Algem’s liquid staking platform was optimized to enable asset staking with liquidity retention for DeFi activities. With Polkadot <a href="/blockchain-development-services" target="_blank">blockchain development services</a>, we enhanced its integration, scalability, and security, providing a robust solution for DeFi users.'
                        ]}
                        reverseOrder={false}
                        alt="Algem"
                        checkItems={algemCheckPoint}
                        openForm={openForm}
                        imageClass="dynamic-polkadot-image"
                    />
                    <DynamicScreens
                        imageSrc={polkaswap}
                        subHeading="Polkaswap"
                        heading="Building a Seamless Decentralized Exchange for the Polkadot Ecosystem"
                        subPoints={[
                            'We enhanced Polkaswap’s AMM DEX to support efficient liquidity pools and cross-chain transactions within the Polkadot-Kusama ecosystem. Using Polkadot Parachain Development, we improved the platform’s performance and security, offering a reliable trading environment.'
                        ]}
                        reverseOrder={true}
                        alt="Polkaswap"
                        checkItems={polkaswapCheckPoint}
                        openForm={openForm}
                        imageClass="dynamic-polkadot-image"
                    />
                </MUIContainer>
            </Grid>
            <Box className="bottom-spacing mobile-bottom-padding">
                <LatestCTA
                    heading="Ready to Build Next-Gen Applications With Polkadot's Interoperability?"
                    buttonText="Talk To Our Experts"
                    openForm={openForm}
                    className="blue-background-cta"
                />
            </Box>
            <Grid item xs={12} className="blockchain-revolution-service  bottom-spacing  mobile-bottom-padding">
                <MUIContainer>
                    <Platforms
                        data={polkadotIndustryEngineering}
                        heading="Transforming Industries with Polkadot Blockchain Development"
                        subheading="As a trusted Polkadot Blockchain Development Company, we enable industries to harness blockchain technology for scalable, secure, and interoperable solutions. Our custom Polkadot development services streamline processes, enhance cross-chain functionality, and deliver tailored solutions for diverse business needs."
                        containerClass="blockchain-card-container"
                        className="platform-key-industry-service no-border"
                    />
                </MUIContainer>
            </Grid>
            <Box className="mobile-bottom-padding bottom-spacing">
                <LatestCTA
                    heading="Need Expert Support to Build Reliable and Scalable Blockchain Networks?"
                    buttonText="Speak To Our Specialists"
                    openForm={openForm}
                    className="blue-background-cta"
                />
            </Box>
            <Box className="helper_section_parent top-space mobile-top-padding mobile-bottom-padding bottom-spacing">
                <FlexibleCards
                    heading="Flexible Engagement Models for Polkadot Blockchain Development"
                    subheading="As a leading Polkadot Blockchain Development Company, we offer flexible engagement models tailored to diverse business needs. Whether you’re seeking full-cycle development or iterative support, our models ensure efficiency, transparency, and value-driven solutions."
                    contentData={flexiblePolkadotContent}
                />
            </Box>
            <Box className="bottom-spacing top-space mobile-top-padding  process-mobile-bottom-padding">
                <MUIContainer>
                    <Box className="spectrum-header-wrapper solutions">
                        <Typography className="spectrum-heading color-black text-align-left" component="h2">
                            From Vision to Deployment: The Polkadot Blockchain Development Process{' '}
                        </Typography>
                        <Box className="divder-wrapper">
                            <hr className="divider-line divider-line-left" />
                        </Box>
                        <Box className="sub-heading-box" pb={4}>
                            <Typography className="spectrum-sub-heading color-black spacing text-align-left" component="p">
                                Building on the Polkadot ecosystem involves a structured process to ensure scalability, interoperability,
                                and seamless integration. Below is an overview of how we craft Polkadot-based blockchain solutions tailored
                                to your business needs:
                            </Typography>
                        </Box>
                    </Box>
                    <Box textAlign="center">
                        <DisplayImage
                            src={development}
                            alt="The Polkadot Blockchain Development Process"
                            className="approach-main-img approach-web-img"
                        />
                    </Box>
                    <Box className="approach-main-img approach-mobile-img">
                        <MobileProcess mobileProcess={palkadotDevelopmentProcess} />
                    </Box>
                </MUIContainer>
            </Box>
            <OurMakeChoice
                heading="Why Choose Debut Infotech"
                subheading="as Your Polkadot Blockchain Development Partner?"
                body={palkadotChooseBody}
                contentData={palkadotChooseContent}
            />
            <Grid item xs={12} className="bottom-spacing">
                <LetsDiscuss
                    heading="Ready to Transform Your Business with Polkadot Blockchain?"
                    subheading="Elevate your operations with custom Polkadot blockchain development solutions designed to enhance scalability, ensure interoperability, and deliver future-ready blockchain ecosystems."
                    btnLabel="Contact Us Today"
                    openForm={() => {
                        globalThis.open('/contact-us', '_blank');
                    }}
                />
            </Grid>
            <Grid item xs={12} className="question-parent top-space mobile-top-padding">
                <MUIContainer>
                    <Questions accordianData={palkadotBlockchainFAQ} />
                </MUIContainer>
            </Grid>
            <Grid item xs={12}>
                <Box className="top-space mobile-top-padding bg-blue-background">
                    <Testimonials heading="Client Testimonials" clientData={palkodotTestimonial} />
                </Box>
            </Grid>
            <script
                type="application/ld+json"
                id="polkadot-blockchain-development"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'FAQPage',
                        mainEntity: [
                            {
                                '@type': 'Question',
                                name: 'What is the Polkadot Blockchain and how does it work?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'Polkadot is a multichain blockchain network that connects different blockchains into a unified ecosystem. It enables seamless data and asset transfers across networks, ensuring scalability, security, and interoperability. By leveraging its Relay Chain and parachains, Polkadot supports decentralized applications, smart contracts, and innovative blockchain solutions for businesses seeking advanced Polkadot Blockchain Services.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'What is Substrate, and how does it relate to Polkadot?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'Substrate is a modular framework used for building blockchains, specifically designed to integrate with Polkadot’s ecosystem. It allows developers to create custom blockchains with advanced features and seamless compatibility with Polkadot’s Relay Chain, enabling faster deployment of custom Polkadot Blockchain Solutions. Substrate is the foundation for most parachains within the Polkadot network.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'What are Polkadot parachains, and why are they important?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: `Polkadot parachains are independent blockchains running parallel to the Relay Chain, providing:
Scalability: Parachains reduce network congestion by processing transactions in parallel.
Interoperability: Enable seamless communication between different chains.
Customizability: Tailored to meet specific business needs through Polkadot Parachain Development.
These features make parachains essential for businesses seeking scalable and secure Polkadot blockchain development services.`
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'How does Polkadot provide security across its network?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: `Polkadot ensures network-wide security through its Relay Chain, which acts as a shared security layer for all connected parachains. Key security features include:
Unified Validator Set: Ensures secure transaction validation across parachains.
Shared Resources: Reduces risks by pooling security for all chains.
This unique model offers robust protection for businesses opting for custom Polkadot development.`
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'Can Polkadot facilitate connections with other blockchain networks?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'Yes, Polkadot’s bridges enable connectivity with external blockchains like Ethereum and Bitcoin, ensuring seamless interoperability. This feature allows businesses to integrate decentralized applications and assets from other networks into their operations. With Polkadot Blockchain Developers, you can create cross-chain solutions tailored to your needs.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'How can integrating Polkadot benefit my business operations?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: `Polkadot offers numerous advantages for businesses:
Interoperability: Connects various blockchain networks for smooth data sharing.
Scalability: Parallel processing reduces transaction bottlenecks.
Security: Unified security model ensures data integrity.
Partnering with a Polkadot Blockchain Development Company helps businesses optimize workflows and enhance efficiency using advanced Polkadot Blockchain Services.`
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'Is Polkadot an ideal choice for building DeFi applications?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'Polkadot is highly suitable for DeFi applications due to its scalability, security, and interoperability. Its parachains enable fast transactions and low fees, while its shared security model ensures safe operations. By opting for Polkadot blockchain development services, businesses can create robust DeFi platforms tailored to their requirements.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'How much does Polkadot development cost?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: "The cost of Polkadot development depends on factors like project complexity, required features, and the development team's expertise. A reputable Polkadot Blockchain Development Company provides transparent pricing, ensuring value for your investment in custom Polkadot Blockchain Solutions."
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'What factors influence the cost of Polkadot projects?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: `Several factors affect the cost of Polkadot projects, including:
Project Scope: The complexity and number of features required.
Development Team: Expertise of Polkadot Blockchain Developers hired.
Integration Needs: Cross-chain compatibility and additional services.

Understanding these factors helps in budgeting effectively for Polkadot Blockchain Services.`
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'How long does it take to develop a Polkadot-based solution?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'The timeline for Polkadot development depends on project requirements and complexity. Basic projects may take weeks, while advanced solutions like Polkadot Parachain Development can require several months. Working with an experienced Polkadot Blockchain Development Company ensures timely delivery and quality solutions.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'Can I get a customized timeline for my Polkadot project?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'Yes, a professional development company will provide a tailored project timeline based on your specific needs. By collaborating with a team offering Polkadot development services, you gain clarity on milestones, deliverables, and expected completion dates, ensuring a smooth development journey.'
                                }
                            }
                        ]
                    })
                }}
            />
        </Box>
    );
};

export default PolkadotBlockchain;
PolkadotBlockchain.Layout = 'Main';
